<template>
  <div v-if="!offerId" class="caption grey--text">{{ $t('offers.attachments.save_first') }}</div>
  <div v-else @dragenter.prevent="onDragEnter" @dragover.prevent @dragleave="onDragLeave" @drop.prevent="onDrop">
    <offer-file-drop-zone :dragging="dragDepth > 0" :progress="progress" class="mb-3" @files="upload" />
    <offer-file-list
      v-if="files.length > 0"
      :files="files"
      :zip="zip"
      :zip-name="$t('offers.attachments.zip_name')"
      @open="open"
      @download="download"
      @remove="remove"
      @remove-folder="removeFolder"
    />
    <div v-else-if="!loading" class="caption grey--text">{{ $t('offers.attachments.none') }}</div>
  </div>
</template>

<script>
import fileApi from '@/api/file';
import quotationApi from '@/api/quotation';
import { openFile, downloadFile } from '@/helpers/fileOpen';
import { droppedFiles } from '@/helpers/droppedFiles';
import OfferFileDropZone from './OfferFileDropZone';
import OfferFileList from './OfferFileList';

/** Tarjouksen liitteet yhtenä listana: tiedostot ja kansiot raahaamalla tai valitsemalla, zip puretaan kansioiksi. */
export default {
  name: 'OfferFiles',
  components: { OfferFileDropZone, OfferFileList },
  props: {
    offerId: Number,
  },
  data() {
    return {
      files: [],
      loading: false,
      progress: null,
      // dragenter/dragleave laukeavat myös lapsielementeistä, joten korostus pidetään päällä laskurilla
      dragDepth: 0,
    };
  },
  watch: {
    offerId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    async load() {
      if (!this.offerId) {
        this.files = [];
        return;
      }
      this.loading = true;
      try {
        this.files = await fileApi.getFiles(this.offerId, 'offer', 'attachments');
      } catch (error) {
        this.$handleApiError(error, this.$t('offers.attachments.load_failed'));
      }
      this.loading = false;
    },
    onDragEnter() {
      this.dragDepth++;
    },
    onDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1);
    },
    async onDrop(event) {
      this.dragDepth = 0;
      this.upload(await droppedFiles(event.dataTransfer));
    },
    async upload(items) {
      if (this.progress || items.length === 0) return;
      const failed = [];
      this.progress = { done: 0, total: items.length, name: '' };
      for (const item of items) {
        this.progress = { ...this.progress, name: item.file.name };
        try {
          await quotationApi.uploadFile(this.offerId, item.file, item.folder);
        } catch (error) {
          failed.push(`${item.file.name} (${this.uploadError(error)})`);
        }
        this.progress = { ...this.progress, done: this.progress.done + 1 };
      }
      this.progress = null;
      await this.load();
      if (failed.length > 0) this.$showErrorNotification(this.$t('offers.attachments.upload_failed_files', { files: failed.join(', ') }));
    },
    // Kokoraja on palvelimella (Spring ja nginx), joten liian iso tiedosto tunnistetaan vastauksesta
    uploadError(error) {
      const response = error.response;
      if (response && response.status === 413) return this.$t('offers.attachments.too_large');
      return (response && response.data && response.data.message) || this.$t('offers.attachments.upload_failed');
    },
    async open(file) {
      try {
        await openFile(this, file);
      } catch (error) {
        this.$handleApiError(error, this.$t('offers.attachments.load_failed'));
      }
    },
    zip(folder) {
      return quotationApi.downloadFilesZip(this.offerId, folder);
    },
    async download(file) {
      try {
        await downloadFile(this, file);
      } catch (error) {
        this.$handleApiError(error, this.$t('offers.attachments.load_failed'));
      }
    },
    async remove(file) {
      if (!(await this.$confirmDelete(this.$t('offers.attachments.delete_confirm', { name: file.filename })))) return;
      try {
        await fileApi.deleteFile(file.key);
        await this.load();
      } catch (error) {
        this.$handleApiError(error, this.$t('offers.attachments.delete_failed'));
      }
    },
    async removeFolder(folder) {
      const files = this.files.filter(f => f.folder === folder.path || (f.folder || '').startsWith(`${folder.path}/`));
      const confirmText = this.$t('offers.attachments.delete_folder_confirm', { name: folder.path, n: files.length });
      if (!(await this.$confirmDelete(confirmText))) return;
      try {
        for (const file of files) await fileApi.deleteFile(file.key);
      } catch (error) {
        this.$handleApiError(error, this.$t('offers.attachments.delete_failed'));
      }
      await this.load();
    },
  },
};
</script>
